import * as React from 'react'
import { useLoaderData, redirect, useNavigate } from 'react-router-dom';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import DnsIcon from '@mui/icons-material/Dns';
import HelpIcon from '@mui/icons-material/Help';
import { assetsApi, contractorsApi, defaultApi, inspectionsApi } from '../../api-interface';
import { AssetResponse, DocumentAIAnalysis, AIVerificationResult, ValidationsResponse, DocumentAppliance, Asset, DocumentDefect, HydratedDocumentAuditEvent, Contractor } from '../../api';
import { Typography, TableContainer, Paper, Table, TableHead, TableRow, TableBody, TableCell, useTheme, Box, IconButton, Modal, FormControl, TextField, Button, Tooltip, Alert, FormLabel, MenuItem, Select } from '@mui/material';
import HeaderTableCell, { SubHeaderTableCell } from '../../components/common/StyledTableCell/StyledTableCell';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import AssetInfoChip from './AssetInfoChip';
import DownloadIcon from '@mui/icons-material/Download';
import { TenantContext } from '../..';
import AddressCorrectionModal from './components/AddressCorrectionModal';
import EditIcon from '@mui/icons-material/Edit';
import FeedbackIcon from '@mui/icons-material/Feedback';
import OverrideModal from './components/OverrideModal';
import OverriddenModal from './components/OverriddenModal';
import { enqueueSnackbar, useSnackbar } from 'notistack';
import ErrorOutlinedIcon from '@mui/icons-material/ErrorOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DocumentLocationModal from './components/DocumentLocationModal';
import HandymanIcon from '@mui/icons-material/Handyman';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';


export async function loader(tenant_id, analysis_id) {
  const analysis = (await inspectionsApi(tenant_id).getAnalysisInspectionsAnalysisIdGet(analysis_id)).data
  const validationsResult: ValidationsResponse = (await inspectionsApi(tenant_id).getInspectionValidationsInspectionsAnalysisIdValidationsGet(analysis_id)).data
  const contractor = (analysis.analysis.contractor_id !== null) ? (await contractorsApi(tenant_id).getContractorContractorsContractorIdGet(String(analysis.analysis.contractor_id))).data : null
  const validations = validationsResult.validations
  const appliances = validationsResult.appliances
  const defects = validationsResult.defects
  const user = await defaultApi.readUsersMeMeGet();
  // Extract contractor feature flag
  const useContractor = user.data.tenants.find((x) => x.id === tenant_id)?.feature_flag_contractors || false;

  let asset: AssetResponse | null = null
  if (analysis.analysis.asset_id !== null) {
    const assets = await assetsApi(tenant_id).getAssetsAssetsGet();
    asset = assets.data.find((x) => x.asset.id == analysis.analysis.asset_id)!
  }
  const user_map = validationsResult.user_mapping
  const ret = { "contractor_flag": useContractor, "contractor": contractor, "analysis": analysis.analysis, "asset": asset, "validations": validations, "appliances": appliances, "defects": defects, "user_map": user_map, "audit_events": analysis.audit_events };
  return ret;
}


const ValidationRow = ({ validation, analysisId, user_map }: { validation: AIVerificationResult, analysisId: string, user_map: { [key: string]: string } }) => {
  const theme = useTheme()
  const [overrideModalOpen, setOverrideModalOpen] = React.useState(false)
  const [overridenModalOpen, setOverridenModal] = React.useState(false)
  const [documentLocationModalOpen, setDocumentLocationModalOpen] = React.useState(false)
  const overriden: boolean = Boolean(validation.overridden_at)
  const overriden_icon = overriden ? <><OverriddenModal user_map={user_map} open={overridenModalOpen} handleClose={() => setOverridenModal(false)} verificationResult={validation}></OverriddenModal><Tooltip title="Overriden"><FeedbackIcon onClick={() => { setOverridenModal(true) }} cursor={"pointer"} /></Tooltip></> : null
  const success_icon = <Typography
    color={theme.palette.success.main}>
    <TaskAltIcon />
    {overriden_icon}
  </Typography>
  const warning_icon = <Typography
    color={theme.palette.warning.main}>
    <ErrorOutlineIcon />
    {overriden_icon}
  </Typography>
  const error_icon = <Typography
    color={theme.palette.error.main}>
    <ErrorOutlineIcon />
    {overriden_icon}
  </Typography>
  const icon = validation.outcome ? success_icon : validation.severity == "error" ? error_icon : warning_icon

  const regulatory = validation.is_regulatory ? <Typography sx={{ verticalAlign: 'middle', display: 'inline' }} color={theme.palette.primary.light}>
    <Tooltip title={<> <b>Regulatory Verification</b> <br />{validation.regulation}</>}><ErrorOutlinedIcon /></Tooltip>
  </Typography>
    :
    <Typography sx={{ verticalAlign: 'middle', display: 'inline' }} color={theme.palette.primary.light}>
      <Tooltip title={<b>Additional Verification</b>}><InfoOutlinedIcon /></Tooltip>
    </Typography>

  const location_icon = validation.location?.length && validation.location.length > 0 ? <Typography sx={{ verticalAlign: 'middle', display: 'inline' }}>
    <Tooltip title={<b>View in document</b>}>
      <VisibilityIcon sx={{ verticalAlign: 'middle', display: 'inline-flex' }} cursor={"pointer"} onClick={() => { setDocumentLocationModalOpen(true) }} />
    </Tooltip>
  </Typography> : <></>

  const reason = validation.overridden_reason ? validation.overridden_reason : validation.original_reason

  return <TableRow
    key={validation.id}
    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
  >
    <TableCell>{regulatory}</TableCell>
    <TableCell>{validation.validation_name}</TableCell>
    <TableCell>{validation.validation_desc}</TableCell>
    <TableCell>{validation.variable_value}</TableCell>
    <TableCell>{icon}</TableCell>
    <TableCell sx={{fontWeight: validation.outcome ? 'regular': 'bold'}}>{String(reason)}</TableCell>
    <TableCell>
      <Typography>
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
          {location_icon}
          <Tooltip title="Override">
            <EditIcon
              cursor="pointer"
              onClick={() => { setOverrideModalOpen(true) }}
              sx={{ verticalAlign: 'middle', display: 'inline-flex' }}
            />
          </Tooltip>

        </Box>
      </Typography>
    </TableCell>
    <OverrideModal analysisId={analysisId} verificationResult={validation} open={overrideModalOpen} handleClose={() => { setOverrideModalOpen(false) }}></OverrideModal>
    <DocumentLocationModal open={documentLocationModalOpen} handleClose={() => { setDocumentLocationModalOpen(false) }} analysisId={analysisId} verificationResult={validation}></DocumentLocationModal>
  </TableRow>
}

const ApplianceSubTable = ({ appliance, appliance_validations, analysisId, user_map }: { appliance: DocumentAppliance, appliance_validations: AIVerificationResult[], analysisId: string, user_map: { [key: string]: string } }) => {
  const appliance_descriptor_arr = [appliance.appliance_location, appliance.appliance_type, appliance.appliance_make, appliance.appliance_model].filter(x => x)
  const appliance_descriptor_joined = appliance_descriptor_arr.join(" - ")
  return <>
    <TableHead>
      <TableRow>
        <SubHeaderTableCell colSpan={7}>{appliance_descriptor_joined}</SubHeaderTableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {appliance_validations.map((validation) => (
        <ValidationRow analysisId={analysisId} validation={validation} user_map={user_map}></ValidationRow>
      ))}
    </TableBody>
  </>
}

const DefectSubTable = ({ defect, defect_validations, analysisId, user_map }: { defect: DocumentDefect, defect_validations: AIVerificationResult[], analysisId: string, user_map: { [key: string]: string } }) => {
  return <>
    <TableHead>
      <TableRow>
        <SubHeaderTableCell colSpan={7}>{defect.defect}</SubHeaderTableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {defect_validations.map((validation) => (
        <ValidationRow analysisId={analysisId} validation={validation} user_map={user_map}></ValidationRow>
      ))}
    </TableBody>
  </>
}

const ChangeContractorModal = ({ open, handleClose, analysisId, currentContractorId }: { open: boolean, handleClose: () => void, analysisId: string, currentContractorId: string | null }) => {
  const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    bgcolor: 'background.paper',
    minWidth: 100,
    p: 4,
  };
  const theme = useTheme()
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate()
  const tenant = React.useContext(TenantContext)
  const [contractor, setContractor] = React.useState<string | null>(currentContractorId)

  const handleUpdate = async (x) => {
    try {
      await inspectionsApi(tenant).setAnalysisContractorInspectionsAnalysisIdContractorPost(analysisId, contractor!)
      enqueueSnackbar("Contrator Updated", { variant: "success" })
      navigate("")
      handleClose()
    } catch (e) {
      if (e?.response?.data?.detail) {
        enqueueSnackbar(e.response.data.detail, { variant: "error" })
      } else {
        enqueueSnackbar("Error: " + e, { variant: "error" })
      }
    }
  }

  // Get a list of contractors from the API to pick from. This will need to be done when the component loads up (useEffect)
  const [contractors, setContractors] = React.useState<Contractor[]>([])
  React.useEffect(() => {
    const fetchContractors = async () => {
      const contractors = await contractorsApi(tenant).getContractorsContractorsGet()
      setContractors(contractors.data)
    }
    fetchContractors()

  }, [])


  return <Modal
    open={open}
    onClose={() => {
      handleClose()
    }}
  >
    <Box sx={style} display="flex" flexDirection={"column"} gap={2}>
      <Typography variant="h6">Update Contractor</Typography>
      <Box display="flex" flexDirection={"column"} gap={2}>
        <FormControl>
          <select value={contractor ? contractor : ""} onChange={(e) => setContractor(e.target.value)} name="contractor" id="contractor" style={{ width: "100%", borderRadius: "4px", border: "1px solid #ccc", minWidth: "200px" }}>
            <option disabled key={""} value={""}>Select a Contractor</option>
            {contractors.map((contractor) => (
              <option key={contractor.id} value={contractor.id}>{contractor.name}</option>
            ))}
          </select>
        </FormControl>
        <Button
          variant="contained"
          onClick={handleUpdate}
          disabled={!contractor}
        >Update</Button>
      </Box>
    </Box>
  </Modal>
}

const ContractorLine = ({ contractor, analysisId }: { contractor: Contractor | null, analysisId: string }) => {
  const navigate = useNavigate()
  const tenant_id = React.useContext(TenantContext)
  const [editModalOpen, setEditModalOpen] = React.useState(false)
  return <Box display={"flex"} flexDirection={"row"} alignItems={"center"} gap={1}>
    <Typography> Contractor: <b>{contractor ? contractor.name : <IconButton sx={{ cursor: 'default', top: -2, lineHeight: 15, padding: 0.0, margin: 0 }}><HelpIcon ></HelpIcon></IconButton>}</b></Typography>
    {contractor && <Tooltip title="Go to Contractor"><IconButton sx={{ top: -2, lineHeight: 15, padding: 0.0, margin: 0 }} onClick={() => navigate("/dashboard/" + tenant_id + "/contractors/" + contractor.id)}><HandymanIcon sx={{ fontSize: 15, lineHeight: 1 }} /></IconButton></Tooltip>}
    <Tooltip title="Edit Contractor"><IconButton sx={{ top: -2, lineHeight: 15, padding: 0.0, margin: 0 }} onClick={() => { setEditModalOpen(true) }}><EditIcon sx={{ fontSize: 15, lineHeight: 1 }} /></IconButton></Tooltip>
    <ChangeContractorModal analysisId={analysisId} currentContractorId={contractor ? contractor.id : null} open={editModalOpen} handleClose={() => { setEditModalOpen(false) }}></ChangeContractorModal>
  </Box>
}

const AnalysisInfoPane = ({ asset, analysis, contractor, contractor_flag }: { asset: Asset | undefined, analysis: DocumentAIAnalysis, contractor: Contractor | null, contractor_flag: Boolean }) => {
  const theme = useTheme();
  const [modal_open, setModalOpen] = React.useState(false);
  const address_sub_arr = asset ? [asset.address_1, asset.address_2, asset.city, asset.county, asset.postcode].filter(x => x) : []
  const asset_sub_joined = asset ? address_sub_arr.join(", ") : <IconButton sx={{ cursor: 'default', top: -2, lineHeight: 15, padding: 0.0, margin: 0 }}><HelpIcon ></HelpIcon></IconButton>
  const inspected = analysis.inspection_date ? new Date(String(analysis.inspection_date)).toLocaleString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : null
  const expired = analysis.next_inspection_due ? new Date() > new Date(String(analysis.next_inspection_due)) : null
  const next_inspection = analysis.next_inspection_due ? new Date(String(analysis.next_inspection_due)).toLocaleString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : null
  const tenant_id = React.useContext(TenantContext)
  const handleClose = () => setModalOpen(false);
  const navigate = useNavigate()
  let assetIcons = <></>
  if (asset) {
    assetIcons = <Box display={"inline-flex"} flexDirection={"row"} alignItems={"center"} gap={1}>
      <Tooltip title="Go to Asset"><IconButton sx={{ top: -2, lineHeight: 15, padding: 0.0, margin: 0 }} onClick={() => navigate("/dashboard/" + tenant_id + "/assets/" + asset.id)}><DnsIcon sx={{ fontSize: 15, lineHeight: 1 }} /></IconButton></Tooltip>
    </Box>
  }

  return <Box>
    <Typography> LGSR Inspection performed at <b>{asset_sub_joined}</b> {assetIcons} <Tooltip title="Change Asset"><IconButton sx={{ top: -2, lineHeight: 15, padding: 0.0, margin: 0 }} onClick={() => { setModalOpen(true) }}><EditIcon sx={{ fontSize: 15, lineHeight: 1 }} /></IconButton></Tooltip></Typography>
    <AddressCorrectionModal analysis={analysis} open={modal_open} handleClose={handleClose}></AddressCorrectionModal>
    <Typography > LGSR Inspection performed on <b>{inspected}</b></Typography>
    <Typography > LGSR  Inspection performed by <b>{String(analysis.engineer_name)}</b></Typography>
    {contractor_flag && <ContractorLine contractor={contractor} analysisId={analysis.id}></ContractorLine>}
    <Typography color={expired ? theme.palette.error.main : theme.palette.text.primary}> This inspection {expired ? <b>expired</b> : "expires"} on <b>{next_inspection}</b></Typography>
  </Box>
}


const AssetAnalysisValidations = () => {
  const data = useLoaderData() as any;
  const validations = data["validations"] as AIVerificationResult[];
  const analysis = data["analysis"] as DocumentAIAnalysis;
  const asset = ("asset" in data) ? data["asset"] as AssetResponse : null;
  const contractor = data["contractor"] as Contractor | null;
  const contractor_flag = data["contractor_flag"] as boolean;
  const appliances = data["appliances"] as DocumentAppliance[];
  const defects = data["defects"] as DocumentDefect[];
  const user_map = data["user_map"] as { [key: string]: string };
  const audit_events = data["audit_events"] as HydratedDocumentAuditEvent[];
  const currentTenant = React.useContext(TenantContext)

  const downloadFile = async () => {
    try {
      const file = await inspectionsApi(currentTenant).getAnalysisFileInspectionsAnalysisIdFileGet(analysis.id, { responseType: 'blob' })
      var link = document.createElement('a');
      link.href = window.URL.createObjectURL(file.data);
      var fileName = analysis.original_filename;
      link.download = fileName;
      link.click();
    } catch (error) {
      enqueueSnackbar("Error downloading file: " + error, { variant: "error" })
    }

  }

  const theme = useTheme();
  return (
    <Box display={"flex"} flexDirection={"column"} gap={3}>
      <Box>
        <Typography variant="h4"> LGSR Report </Typography>
        <Typography variant="subtitle1" fontStyle={"italic"}>{analysis.original_filename}<IconButton onClick={downloadFile}><DownloadIcon fontSize='inherit' /></IconButton></Typography>
      </Box>
      <AnalysisInfoPane asset={asset?.asset} analysis={analysis} contractor={contractor} contractor_flag={contractor_flag} />
      <AssetInfoChip
        left={<Typography  fontWeight={500} fontSize={"1.25rem"}>LGSR Report Status</Typography>}
        right={<Typography whiteSpace={"nowrap"} fontSize={"1.25rem"} color={analysis.outcome == "pass" ? theme.palette.success.main : analysis.outcome == "warning" ? theme.palette.warning.main : theme.palette.error.main} fontWeight={"bold"}>
          {analysis.outcome == "pass" ? <TaskAltIcon fontSize='inherit' /> : <ErrorOutlineIcon fontSize='inherit' />} {analysis.outcome == "pass" ? "Compliant" : analysis.outcome == "warning" ? "Has Warnings" : "Needs Attention"}
        </Typography>}
        bgcolor={analysis.outcome == "pass" ? theme.palette.successBackground.main : analysis.outcome == "warning" ? theme.palette.warningBackground.main : theme.palette.errorBackground.main}
        borderColor={analysis.outcome == "pass" ? theme.palette.successOutline.main : analysis.outcome == "warning" ? theme.palette.warningOutline.main : theme.palette.errorOutline.main}
      />
      <TableContainer component={Paper} >

        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <TableHead >
            <TableRow>
              <HeaderTableCell>Type</HeaderTableCell>
              <HeaderTableCell>Verification Name</HeaderTableCell>
              <HeaderTableCell>Verification Requirement</HeaderTableCell>
              <HeaderTableCell>Identified Value</HeaderTableCell>
              <HeaderTableCell>Outcome</HeaderTableCell>
              <HeaderTableCell>Reason</HeaderTableCell>
              <HeaderTableCell></HeaderTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {validations.filter((x) => ((x.appliance_index === null) && x.defect_index === null)).map((validation) => (
              <ValidationRow analysisId={analysis.id} validation={validation} user_map={user_map}></ValidationRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant='h5' fontWeight={500} fontSize={"1.25rem"}>Appliance Validations</Typography>
      <TableContainer component={Paper} >
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <TableHead >
            <TableRow>
              <HeaderTableCell>Type</HeaderTableCell>
              <HeaderTableCell>Verification Name</HeaderTableCell>
              <HeaderTableCell>Verification Requirement</HeaderTableCell>
              <HeaderTableCell>Identified Value</HeaderTableCell>
              <HeaderTableCell>Outcome</HeaderTableCell>
              <HeaderTableCell>Reason</HeaderTableCell>
              <HeaderTableCell></HeaderTableCell>
            </TableRow>
          </TableHead>
          {
            appliances.length === 0 && <TableRow><HeaderTableCell colSpan={5}><Alert severity="info">No appliances found</Alert></HeaderTableCell></TableRow>
          }
          {
            appliances.map((appliance, appliance_index) => (<ApplianceSubTable user_map={user_map} analysisId={analysis.id} appliance={appliance} appliance_validations={validations.filter((x) => x.appliance_index === appliance_index)} ></ApplianceSubTable>))
          }
        </Table>
      </TableContainer>

      <Typography variant='h5' fontWeight={500} fontSize={"1.25rem"}>Defect Validations</Typography>
      <TableContainer component={Paper} >
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <TableHead >
            <TableRow>
              <HeaderTableCell>Type</HeaderTableCell>
              <HeaderTableCell>Verification Name</HeaderTableCell>
              <HeaderTableCell>Verification Requirement</HeaderTableCell>
              <HeaderTableCell>Identified Value</HeaderTableCell>
              <HeaderTableCell>Outcome</HeaderTableCell>
              <HeaderTableCell>Reason</HeaderTableCell>
              <HeaderTableCell></HeaderTableCell>
            </TableRow>
          </TableHead>
          {
            defects.length === 0 && <TableRow><HeaderTableCell colSpan={6}><Alert severity="info">No defects found</Alert></HeaderTableCell></TableRow>
          }
          {
            defects.map((defect, defect_index) => (<DefectSubTable user_map={user_map} analysisId={analysis.id} defect={defect} defect_validations={validations.filter((x) => x.defect_index === defect_index)} ></DefectSubTable>))
          }
        </Table>
      </TableContainer>

      <Typography variant='h5' fontWeight={500} fontSize={"1.25rem"}>Audit Events</Typography>
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="audit events table">
          <TableHead>
            <TableRow>
              <HeaderTableCell>Timestamp</HeaderTableCell>
              <HeaderTableCell>User</HeaderTableCell>
              <HeaderTableCell>Event</HeaderTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {audit_events && audit_events.length > 0 ? (
              audit_events.map((event, idx) => {
                const e = event.document_audit_event;
                const user = (event.user_first_name || event.user_last_name) ? `${event.user_first_name || ''} ${event.user_last_name || ''}`.trim() : event.user_id;
                return (
                  <TableRow key={idx}>
                    <TableCell>{e.timestamp ? new Date(e.timestamp).toLocaleString('en-GB', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : ''}</TableCell>
                    <TableCell>{user}</TableCell>
                    <TableCell>{e.audit_description || ''}</TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow><TableCell colSpan={3}><Alert severity="info">No audit events found</Alert></TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

    </Box>
  )
}

export default AssetAnalysisValidations